/**
 * Tailwind/CSS implementation of the Pagination component.
 * Uses Seeds pagination CSS classes from pagination.css.
 */

import { Select } from "@sproutsocial/seeds-react-select";
import { Icon } from "@sproutsocial/seeds-react-icon";
import { Text } from "@sproutsocial/seeds-react-text";
import type { PaginationProps } from "./PaginationTypes";
import { usePaginationModel } from "./usePaginationModel";

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

export const PaginationTailwind = (props: PaginationProps) => {
  const { pageSize, totalRows, onPageChange, className } = props;
  const {
    displayType,
    pageSizeOptions,
    tableCountFunction,
    totalPages,
    currentPage,
    startRow,
    endRow,
    canGoPrevious,
    canGoNext,
    handlePrevious,
    handleNext,
    handlePageSizeChange,
    pageNumbers,
  } = usePaginationModel(props);

  if (totalPages <= 1 && !pageSizeOptions) {
    // No pagination needed if only one page and no page size options
    return null;
  }

  return (
    <div className={cn("seeds-pagination-container", className)}>
      <Text className={cn("seeds-pagination-info")}>
        {tableCountFunction({
          startNum: startRow,
          endNum: endRow,
          totalRowCount: totalRows,
        })}
      </Text>
      {/* Page size selector */}
      {pageSizeOptions && displayType === "dropdown" && (
        <div className={cn("seeds-pagination-page-size-selector")}>
          <Select
            id="page-size-select"
            name="page-size"
            value={String(pageSize)}
            onChange={handlePageSizeChange}
            ariaLabel="Rows per page"
            size="small"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </Select>
        </div>
      )}

      {/* TODO: Add inline page size selector for displayType === 'inline' */}

      {/* Page navigation controls */}
      {totalPages > 1 && (
        <div className={cn("seeds-pagination-controls")}>
          <div className={cn("seeds-pagination-page-buttons")}>
            <button
              type="button"
              className={cn("seeds-pagination-button")}
              onClick={handlePrevious}
              disabled={!canGoPrevious}
              aria-label="Previous page"
            >
              <Icon name="chevron-left-outline" />
            </button>

            {pageNumbers.map((pageNum, index) => {
              if (pageNum === "...") {
                return (
                  <button
                    type="button"
                    key={`ellipsis-${index}`}
                    className={cn("seeds-pagination-button")}
                    disabled
                    aria-label="More pages"
                  >
                    <Icon name="ellipsis-horizontal-outline" />
                  </button>
                );
              }

              const page = pageNum as number;
              const isCurrentPage = page === currentPage;

              return (
                <button
                  type="button"
                  key={page}
                  className={cn("seeds-pagination-button", {
                    "seeds-pagination-button-selected": isCurrentPage,
                  })}
                  onClick={() => onPageChange(page - 1)}
                  disabled={isCurrentPage}
                  aria-label={`Page ${page}`}
                  aria-current={isCurrentPage ? "page" : undefined}
                >
                  {page}
                </button>
              );
            })}

            <button
              type="button"
              className={cn("seeds-pagination-button")}
              onClick={handleNext}
              disabled={!canGoNext}
              aria-label="Next page"
            >
              <Icon name="chevron-right-outline" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
